import DateFnsUtils from "@date-io/date-fns";
import {
  DatePickerView,
  KeyboardDatePicker,
  KeyboardDatePickerProps,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import { CalendarIcon } from "Components/Shared/Icons/CalendarIcon";
import { StyledInputLabel } from "Components/Shared/StyledComponents";
import { isValid, formatISO } from "date-fns";
import csLocale from "date-fns/locale/cs";
import { FieldErrorProps, useFieldError } from "Hooks/useFieldError";
import * as React from "react";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

type Props = FieldErrorProps & {
  value?: Date | string | null;
  label?: string;
  placeholder?: string;
  onChange?: (date: string | null) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  isDisabled?: boolean;
  format?: string;
  views?: DatePickerView[];
};

const StyledDatePicker = styled(
  ({ hasError, ...rest }: KeyboardDatePickerProps & { hasError: boolean }) => (
    <KeyboardDatePicker {...rest} />
  ),
)`
  .ibis-MuiInputBase-input {
    padding: 9px;
  }

  .ibis-MuiOutlinedInput-adornedEnd {
    padding-right: ${props => props.theme.spacing(1)}px;
  }
  .ibis-MuiOutlinedInput-notchedOutline {
    border-color: ${props =>
      props.hasError ? props.theme.colors.red : props.theme.colors.border};
  }

  .ibis-MuiIconButton-root {
    width: 25px;
    height: 25px;
    padding: 2px;
  }

  .ibis-MuiFormHelperText-contained {
    position: absolute;
    top: 100%;
  }
`;

const StyledCalendarIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 23px;
    height: 23px;
  }
`;

export const DatePicker: React.FunctionComponent<Props> = props => {
  const { hasError, originalProps } = useFieldError(props);
  const {
    label,
    value,
    onChange,
    placeholder,
    minDate,
    maxDate,
    isDisabled,
    format,
    views,
  } = originalProps;
  const { t } = useResource();

  const [popupOpen, setPopupOpen] = React.useState(false);

  const handleChange = (date: Date | null) => {
    onChange && onChange(date && isValid(date) ? formatISO(date) : null);
    setPopupOpen(false);
  };

  return (
    <div>
      {label && (
        <StyledInputLabel hasError={hasError} className={"form-label"}>
          {label}
        </StyledInputLabel>
      )}
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={csLocale}>
        <StyledDatePicker
          hasError={hasError}
          minDate={minDate}
          maxDate={maxDate}
          minDateMessage={t(Resources.Common.DatePicker.MinDate, {
            minDate: formatDate(minDate ?? ""),
          })}
          variant={"inline"}
          format={format ?? "dd.MM.yyyy"}
          margin={"none"}
          placeholder={placeholder}
          fullWidth
          value={value}
          onChange={handleChange}
          open={popupOpen}
          disabled={isDisabled}
          onOpen={() => setPopupOpen(true)}
          PopoverProps={{
            onBackdropClick: () => setPopupOpen(false),
          }}
          inputVariant={"outlined"}
          keyboardIcon={
            <StyledCalendarIcon>
              <CalendarIcon />
            </StyledCalendarIcon>
          }
          KeyboardButtonProps={{
            "aria-label": "change date",
          }}
          views={views}
        />
      </MuiPickersUtilsProvider>
    </div>
  );
};
